import { Injectable } from '@angular/core';
import {
  AllowedDevice,
  DeviceManufacturer,
  DeviceModel,
  DeviceSeries,
  EnhancedDeviceManufacturer,
  EnhancedDeviceSeries,
} from '@app/core/interfaces/allowed-device';
import { DeviceSpecification, DeviceSpecificationType } from '@app/core/interfaces/c5/device-specification';
import { DomainCompactInfo } from '@app/core/interfaces/c5/domain-compact-info';
import { PhoneRedirectionInfo } from '@app/core/interfaces/phone-redirection-info';
import { SystemFiles } from '@app/core/interfaces/portal/system-files';
import { UserLevel } from '@app/core/models/user-level';
import { AllowedDeviceRepository } from '@app/core/repositories/allowed-device.repository';
import { DeviceFirmwareRepository } from '@app/core/repositories/device-firmware.repository';
import { DeviceManufacturerRepository } from '@app/core/repositories/device-manufacturer.repository';
import { DeviceModelRepository } from '@app/core/repositories/device-model.repository';
import { DeviceSeriesRepository } from '@app/core/repositories/device-series.repository';
import { DeviceSpecificationsRepository } from '@app/core/repositories/device-specifications-repository';
import { FileRepository } from '@app/core/repositories/file.repository';
import { SystemRepository } from '@app/core/repositories/system.repository';
import { ApplicationEventService, DownloadChanged } from '@app/core/services/application-events';
import { cached, Cached, cachedGroup } from '@app/utils/cached';
import { toMap } from '@app/utils/common';
import { sortBy } from '@app/utils/sort/sort-by';
import {
  EnhancedDeviceFirmware,
  EnhancedDeviceModel,
} from '@business/allowed-device/models/allowed-device-view-model';
import { of } from 'rxjs';
import { map } from 'rxjs/operators';
import { CachedHelperService } from '../cached-helper.service';

@Injectable({
  providedIn: 'root',
})
export class SystemContextService {
  private _allowedDevices: Cached<AllowedDevice[]> | null = null;

  get allowedDevices(): Cached<AllowedDevice[]> {
    this._allowedDevices ??= cached({
      dataProducer: () =>
        this.allowedDeviceRepository
          .listSystemAllowedDevices()
          .pipe(map((allowedDevices) => allowedDevices.sort(sortBy(['deviceName', 'firmware'])))),
      id: 'system-context:allowed-devices',
    });

    return this._allowedDevices;
  }

  private _deviceFirmwares: Cached<DeviceFirmwareViewModel> | null = null;

  get deviceFirmwares(): Cached<DeviceFirmwareViewModel> {
    this._deviceFirmwares ??= cachedGroup(
      [this.enhancedDeviceModels],
      ([deviceModels]) => {
        return this.deviceFirmwareRepository.list().pipe(
          map((firmwares) => {
            const enhancedFirmwares: EnhancedDeviceFirmware[] = firmwares.map((firmware) => {
              const model = deviceModels.modelMap[firmware.modelId];

              return {
                ...firmware,
                model,
              };
            });

            return {
              firmwareMap: toMap(enhancedFirmwares, 'id'),
              firmwares: enhancedFirmwares,
            };
          })
        );
      },
      {
        id: 'system-context:device-firmwares',
      }
    );

    return this._deviceFirmwares;
  }

  private _deviceManufacturers: Cached<DeviceManufacturersViewModel> | null = null;

  get deviceManufacturers(): Cached<DeviceManufacturersViewModel> {
    this._deviceManufacturers ??= cached({
      clearDelay: 1000 * 60 * 60,
      dataProducer: () =>
        this.deviceManufacturerRepository.list().pipe(
          map((manufacturers) => {
            return {
              manufacturers,
              manufacturerMap: toMap(manufacturers, 'id'),
            };
          })
        ),
      id: 'system-context:device-manufacturers',
    });

    return this._deviceManufacturers;
  }

  private _deviceModels: Cached<DeviceModelsViewModel> | null = null;

  get deviceModels(): Cached<DeviceModelsViewModel> {
    this._deviceModels ??= cached({
      dataProducer: () =>
        this.deviceModelRepository.list().pipe(
          map((models) => {
            return {
              models,
              modelsMap: toMap(models, 'id'),
            };
          })
        ),
      id: `system-context:device-models`,
    });

    return this._deviceModels;
  }

  private _deviceSeries: Cached<DeviceSeriesViewModel> | null = null;

  get deviceSeries(): Cached<DeviceSeriesViewModel> {
    this._deviceSeries ??= cached({
      clearDelay: 1000 * 60 * 60,
      dataProducer: () =>
        this.deviceSeriesRepository.list().pipe(
          map((series) => {
            return {
              series,
              seriesMap: toMap(series, 'id'),
            };
          })
        ),
      id: 'system-context:device-series',
    });

    return this._deviceSeries;
  }

  private _deviceSpecs: Cached<DeviceSpecification[]> | null = null;

  get deviceSpecs(): Cached<DeviceSpecification[]> {
    this._deviceSpecs ??= cached({
      clearDelay: 1000 * 60 * 60,
      dataProducer: () =>
        this.deviceSpecificationsRepository.list().pipe(
          map((deviceSpecifications) =>
            // Nobody cares about or understands IAD_MASTER (Roland L.)
            deviceSpecifications.filter(
              (specification) => specification.devSpecType !== DeviceSpecificationType.IAD_MASTER
            )
          )
        ),
      id: 'system-context:device-specs',
    });

    return this._deviceSpecs;
  }

  private _enhancedDeviceManufacturers: Cached<EnhancedDeviceManufacturersViewModel> | null = null;

  get enhancedDeviceManufacturers(): Cached<EnhancedDeviceManufacturersViewModel> {
    this._enhancedDeviceManufacturers ??= cachedGroup(
      [this.deviceManufacturers, this.deviceSpecs],
      ([{ manufacturers }, deviceSpecs]) => {
        const enhancedManufacturers = manufacturers.map((manufacturer): EnhancedDeviceManufacturer => {
          return {
            ...manufacturer,
            deviceSpecification: deviceSpecs.find(
              (specification) => specification.id === manufacturer.c5DeviceSpecId
            ),
          };
        });

        return of({
          manufacturers: enhancedManufacturers,
          manufacturerMap: toMap(enhancedManufacturers, 'id'),
        });
      },
      {
        clearDelay: 1000 * 60 * 60,
        id: 'system-context:enhanced-device-manufacturers',
      }
    );

    return this._enhancedDeviceManufacturers;
  }

  private _enhancedDeviceModels: Cached<EnhancedDeviceModelsViewModel> | null = null;

  get enhancedDeviceModels(): Cached<EnhancedDeviceModelsViewModel> {
    this._enhancedDeviceModels ??= cachedGroup(
      [this.deviceModels, this.deviceManufacturers, this.deviceSeries, this.phoneRedirectionInfo],
      ([{ models }, { manufacturerMap }, { seriesMap }, phoneRedirectionInfo]) => {
        const modelsWithDisplayName = models.map((model): EnhancedDeviceModel => {
          const series = seriesMap[model.seriesId];
          const manufacturer = manufacturerMap[series.manufacturerId];
          const matchingRedirection = phoneRedirectionInfo.find(
            (info) => info.id === model.phoneRedirectInfoId
          );

          return {
            ...model,
            displayName: model.displayName ?? `${manufacturer.manufacturer} ${model.model}`,
            manufacturer,
            series,
            seriesName: seriesMap[model.seriesId].series,
            phoneRedirectInfoServiceName: matchingRedirection?.serviceName ?? null,
          };
        });

        return of({
          models: modelsWithDisplayName,
          modelMap: toMap(modelsWithDisplayName, 'id'),
        } satisfies EnhancedDeviceModelsViewModel);
      },
      {
        clearDelay: 1000 * 60 * 60,
        id: 'system-context:device-models',
      }
    );

    return this._enhancedDeviceModels;
  }

  private _enhancedDeviceSeries: Cached<EnhancedDeviceSeriesViewModel> | null = null;

  get enhancedDeviceSeries(): Cached<EnhancedDeviceSeriesViewModel> {
    this._enhancedDeviceSeries ??= cachedGroup(
      [this.deviceSeries, this.enhancedDeviceManufacturers],
      ([{ series }, { manufacturerMap }]) => {
        const enhancedSeries = series.map((series): EnhancedDeviceSeries => {
          const manufacturer = manufacturerMap[series.manufacturerId];

          return {
            ...series,
            manufacturer: manufacturer,
            manufacturerName: manufacturer.manufacturer,
          };
        });

        return of({
          series: enhancedSeries,
          seriesMap: toMap(enhancedSeries, 'id'),
        } satisfies EnhancedDeviceSeriesViewModel);
      },
      {
        id: `system-context:enhanced-device-series`,
      }
    );

    return this._enhancedDeviceSeries;
  }

  private _phoneRedirectionInfo: Cached<PhoneRedirectionInfo[]> | null = null;

  get phoneRedirectionInfo(): Cached<PhoneRedirectionInfo[]> {
    this._phoneRedirectionInfo ??= this.cachedHelperService.routeAwareCached({
      clearDelay: 1000 * 60 * 10,
      dataProducer: () => this.allowedDeviceRepository.listPhoneRedirectionInfo(),
      id: ([serviceProviderId]) => `service-provider:${serviceProviderId}:phone-redirection-info`,
      routeParameters: ['serviceProviderId'],
    });

    return this._phoneRedirectionInfo;
  }

  private _files: Cached<SystemFiles> | null = null;

  get files(): Cached<SystemFiles> {
    this._files ??= cached({
      dataProducer: () => this.fileRepository.listSystemFiles(UserLevel.SystemAdmin),
      id: `system:files`,
    });

    return this._files;
  }

  private _serviceProviders: Cached<DomainCompactInfo[]> | null = null;

  get serviceProviders(): Cached<DomainCompactInfo[]> {
    this._serviceProviders ??= cached({
      dataProducer: () => {
        return this.systemRepository.listServiceProviders();
      },
      id: `system:service-providers`,
    });

    return this._serviceProviders;
  }

  constructor(
    private allowedDeviceRepository: AllowedDeviceRepository,
    private applicationEventService: ApplicationEventService,
    private deviceFirmwareRepository: DeviceFirmwareRepository,
    private deviceManufacturerRepository: DeviceManufacturerRepository,
    private deviceModelRepository: DeviceModelRepository,
    private deviceSeriesRepository: DeviceSeriesRepository,
    private deviceSpecificationsRepository: DeviceSpecificationsRepository,
    private fileRepository: FileRepository,
    private systemRepository: SystemRepository,
    private cachedHelperService: CachedHelperService
  ) {
    this.applicationEventService.on$(DownloadChanged);
  }
}

export interface DeviceFirmwareViewModel {
  firmwareMap: Record<number, EnhancedDeviceFirmware>;
  firmwares: EnhancedDeviceFirmware[];
}

export interface DeviceManufacturersViewModel {
  manufacturers: DeviceManufacturer[];
  manufacturerMap: Record<number, DeviceManufacturer>;
}

export interface DeviceModelsViewModel {
  models: DeviceModel[];
  modelsMap: Record<number, DeviceModel>;
}

export interface DeviceSeriesViewModel {
  series: DeviceSeries[];
  seriesMap: Record<number, DeviceSeries>;
}

export interface EnhancedDeviceManufacturersViewModel {
  manufacturers: EnhancedDeviceManufacturer[];
  manufacturerMap: Record<number, EnhancedDeviceManufacturer>;
}

export interface EnhancedDeviceModelsViewModel {
  models: EnhancedDeviceModel[];
  modelMap: Record<number, EnhancedDeviceModel>;
}

export interface EnhancedDeviceSeriesViewModel {
  series: EnhancedDeviceSeries[];
  seriesMap: Record<number, EnhancedDeviceSeries>;
}
